// AI Generated Component
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageAccessibilityRepoService } from '@client/app/services/api/page-accessibility/page-accessibility-repo.service';
import { SessionService } from '@monsido/core/session/session.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { AccessibilityFiltersService } from '@client/app/modules/accessibility/services/filters/accessibility-filters.service';
import { AccessibilityRepoService } from '@client/app/services/api/accessibility-repo/accessibility-repo.service';
import { CrawlHistoryInfoService } from '@client/app/core/services/crawl-history-info/crawl-history-info.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';

@Component({
    selector: 'page-details-section-accessibility',
    standalone: false,
    template: `
        <div id="page-details-accessibility">
            <div class="row page-details-section-header">
                <div class="col-48">
                    <page-header [icon]="['MENUES', 'ACCESSIBILITY'] | monIcons"
                                 [header]="header">
                        <page-header-content>
                            <div class="d-flex align-items-center flex-wrap justify-content-end">
                                <div class="mr-1 mr-md-0 order-2 order-md-1 text-nowrap mb-4 mb-md-0">
                                    <csv-export-button [exports]="exports" clsMargin="ml-1" clsSize="py-2 px-3"></csv-export-button>
                                </div>
                                <table-compliance
                                        class="pr-md-2 ml-sm-5 order-1 order-md-2"
                                        [data]="[allIssues - checksWithErrors, checksWithErrors]"
                                        [labels]="['', '']"
                                        size="46"
                                        [btnWord]="'Overall accessibility compliance level for this page' | translate"
                                        [indexNumber]="0">
                                </table-compliance>
                            </div>
                        </page-header-content>
                    </page-header>
                </div>
            </div>
            <div class="page-details-section-body">
                <page-accessibility-wcag2-0 [active]="active"
                                           *ngIf="'wcag2' | accessibilityHasGuideline"
                                           [page]="page"
                                           [tab]="tab"
                                           [filters]="filters"
                                           [availableFilters]="availableFilters"
                                           (tabChange)="onTabChange($event)"
                                           [issues]="issues"
                                           [progress]="progress"
                                           (refreshChecks)="refreshChecks()"
                                           [showIgnoredChecks]="allowIgnoreAction">
                </page-accessibility-wcag2-0>
                <page-accessibility-wcag21 [active]="active"
                                           *ngIf="'wcag21' | accessibilityHasGuideline"
                                           [page]="page"
                                           [tab]="tab"
                                           [filters]="filters"
                                           [availableFilters]="availableFilters"
                                           (tabChange)="onTabChange($event)"
                                           [issues]="issues"
                                           [progress]="progress"
                                           (refreshChecks)="refreshChecks()"
                                           [showIgnoredChecks]="allowIgnoreAction">
                </page-accessibility-wcag21>
                <page-accessibility-wcag2-2 [active]="active"
                                           *ngIf="'wcag22' | accessibilityHasGuideline"
                                           [page]="page"
                                           [tab]="tab"
                                           [filters]="filters"
                                           [availableFilters]="availableFilters"
                                           (tabChange)="onTabChange($event)"
                                           [issues]="issues"
                                           [progress]="progress"
                                           (refreshChecks)="refreshChecks()"
                                           [showIgnoredChecks]="allowIgnoreAction">
                </page-accessibility-wcag2-2>
                <page-accessibility-wcag2-2 [active]="active"
                                           *ngIf="'qualweb' | accessibilityHasGuideline"
                                           [page]="page"
                                           [tab]="tab"
                                           [filters]="filters"
                                           [availableFilters]="availableFilters"
                                           (tabChange)="onTabChange($event)"
                                           [issues]="issues"
                                           [progress]="progress"
                                           (refreshChecks)="refreshChecks()"
                                           [showIgnoredChecks]="allowIgnoreAction">
                </page-accessibility-wcag2-2>
            </div>
        </div>
    `,
})
export class PageDetailsSectionAccessibilityComponent implements OnInit, OnDestroy {
    @Input() page: any;
    @Input() active: any;
    @Output() refreshPage = new EventEmitter<any>();

    private destroyed$ = new Subject<void>();
    header = '';
    allowIgnoreAction = false;
    allIssues = 0;
    checksWithErrors = 0;
    tab: any = '';
    availableFilters: any = {};
    filters: any = {};
    issues: any = {};
    progress: any = null;
    exports: any[] = [];

    private accessibilityErrorsOnly = true;

    constructor (
        private pageAccessibilityRepoService: PageAccessibilityRepoService,
        private sessionService: SessionService,
        private translateService: TranslateService,
        private accessibilityFiltersService: AccessibilityFiltersService,
        private accessibilityRepoService: AccessibilityRepoService,
        private crawlHistoryInfoService: CrawlHistoryInfoService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    ngOnDestroy (): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private activate (): void {
        this.crawlHistoryInfoService.accessibilityErrorsOnly$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                this.accessibilityErrorsOnly = value;
            });

        if (this.pipeService.get('accessibilityHasGuideline')('wcag2')) {
            this.header = this.translateService.getString('WCAG 2.0 Accessibility Compliance');
        } else if (this.pipeService.get('accessibilityHasGuideline')('wcag21')) {
            this.header = this.translateService.getString('WCAG 2.1 Accessibility Compliance');
        } else if (this.pipeService.get('accessibilityHasGuideline')('wcag22')) {
            this.header = this.translateService.getString('WCAG 2.2 Accessibility Compliance');
        } else if (this.pipeService.get('accessibilityHasGuideline')('qualweb')) {
            this.header = this.translateService.getString('QUALWEB Accessibility Compliance');
        }

        this.accessibilityRepoService.getSummary().then((summary) => {
            this.allIssues = summary.checks;
            this.checksWithErrors = this.page.accessibility_checks_with_errors_count;
        });

        this.tab = '';
        this.setupFilters();
        this.refreshChecks();
        this.setExports();
    }

    private setupFilters (): void {
        this.availableFilters = {};
        if (!this.accessibilityErrorsOnly) {
            this.availableFilters.type = this.accessibilityFiltersService.getTypes();
        }
        this.availableFilters[this.accessibilityFiltersService.getResponsibilityTranslation()] =
            this.accessibilityFiltersService.getResponsibilities();
        this.filters = {};
        this.filters.type = [];
        this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()] = [];
    }

    refreshChecks (): void {
        this.progress = this.getPageChecks().then((checks) => {
            const sortedChecks = this.sortChecks(checks);
            if (this.active === null && sortedChecks.length > 0) {
                this.active = sortedChecks[0].id;
            }
            this.groupByStandard(sortedChecks);
        });
    }

    onTabChange (tab: any): void {
        this.tab = tab instanceof Object ? tab.tab : tab;
        this.refreshChecks();
        this.setExports();
    }

    private getPageChecks (): Promise<any[]> {
        const params: any = {
            page_size: 0,
            abbr: !['passed_checks', 'ignored_issues', ''].includes(this.tab) ? this.tab : null,
        };
        const isFilteringIssuesByType = this.filters.type.length > 0;
        const isFilteringMobileIssues = isFilteringIssuesByType && this.filters.type[0].value === 'mobile';

        if (isFilteringIssuesByType && !isFilteringMobileIssues) {
            params.type = this.filters.type[0].value;
        }

        if (this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()].length > 0) {
            const selectedResponsibility =
                this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()][
                    this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()].length - 1
                ];
            this.filters[this.accessibilityFiltersService.getResponsibilityTranslation()] = [selectedResponsibility];
            params.responsibility = selectedResponsibility.value;
        }

        return this.pageAccessibilityRepoService.getAllChecks(this.page.id, params).then((data) => {
            this.allowIgnoreAction = !this.sessionService.isDomainGroup();

            if (isFilteringMobileIssues) {
                return data.filter((check: any) => {
                    return check.mobile === true;
                });
            }

            return data;
        });
    }

    private sortChecks (checks: any[]): any[] {
        if (!Array.isArray(checks)) {
            return [];
        }

        return checks.sort((a, b) => {
            if (a.ignored || b.ignored) {
                if (a.ignored && !b.ignored) {
                    return 1;
                } else if (!a.ignored && b.ignored) {
                    return -1;
                }
            }

            if (a.errors_count === 0 && b.errors_count > 0) {
                return 1;
            } else if (b.errors_count === 0 && a.errors_count > 0) {
                return -1;
            }
            return a.issue_number < b.issue_number ? -1 : a.issue_number > b.issue_number ? 1 : 0;
        });
    }

    private groupByStandard (checks: any[]): void {
        this.wcag20(checks);
    }

    private wcag20 (checks: any[]): void {
        this.issues = {};
        this.issues.A = checks.filter((data) => {
            return data.issue_abbr === 'A';
        });
        this.issues.AA = checks.filter((data) => {
            return data.issue_abbr === 'AA';
        });
        this.issues.AAA = checks.filter((data) => {
            return data.issue_abbr === 'AAA';
        });
    }

    private setExports (): void {
        const categoryRef = [this.page.id];
        if (this.tab !== '') {
            categoryRef.push(this.tab);
        }

        this.exports = [
            {
                name: this.translateService.getString('Accessibility'),
                data: {
                    category: 'accessibility_checks_for_page',
                    category_ref: categoryRef.join(','),
                },
            },
        ];
    }
}
